import { useRef, useState, type CSSProperties, type DragEvent } from "react";
import { useAuth } from "@clerk/react";
import {
  Check,
  Eye,
  EyeOff,
  GripVertical,
  ImagePlus,
  Images,
  LayoutDashboard,
  MoveLeft,
  MoveRight,
  Palette,
  Pencil,
  Share2,
  Star,
  Trash2,
  Undo2,
  Upload
} from "lucide-react";
import { authedFetch, fetchGalleryData, readJson, useGalleryData, type GalleryData } from "./api";
import { FramedPhoto, ScrapbookGallery } from "./Frames";
import { defaultTheme, photoRatio, type GalleryTheme, type Photo } from "./model";
import { AuthControls, ErrorView, LoadingView, ThemedShell } from "./ui";
import "./deletion.css";

export function GalleryPage({ slug, initialData }: { slug: string; initialData?: GalleryData }) {
  const { getToken } = useAuth();
  const { gallery, photos, setPhotos, error, loading } = useGalleryData(slug, initialData);
  const [managing, setManaging] = useState(false);
  const [status, setStatus] = useState("");
  const [deletedPhotos, setDeletedPhotos] = useState<Photo[]>([]);
  const [galleryDeleted, setGalleryDeleted] = useState(false);
  const [busy, setBusy] = useState(false);
  const mutationPending = useRef(false);
  const draggedId = useRef<string | null>(null);

  if (loading) return <LoadingView />;
  if (error || !gallery) return <ErrorView message={error || "Gallery not found"} />;

  const theme = gallery.theme || defaultTheme;
  const visiblePhotos = photos.filter((photo) => !photo.isHidden);
  const displayPhotos = managing ? photos : visiblePhotos;

  function beginMutation() {
    if (mutationPending.current) return false;
    mutationPending.current = true;
    setBusy(true);
    return true;
  }

  function endMutation() {
    mutationPending.current = false;
    setBusy(false);
  }

  async function shareGallery() {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setStatus("Gallery link copied");
    } catch {
      setStatus("Copy the link from your browser to share this gallery");
    }
  }

  async function saveOrder(nextPhotos: Photo[], previousPhotos: Photo[]) {
    if (!beginMutation()) return;
    setPhotos(nextPhotos);
    setStatus("Saving order...");
    try {
      const response = await authedFetch(getToken, `/api/galleries/${slug}/photos/curate`, {
        method: "PATCH",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ orderedPhotoIds: nextPhotos.map((photo) => photo.id) })
      });
      await readJson(response);
      setStatus("Order saved");
    } catch (err) {
      setPhotos(previousPhotos);
      setStatus(err instanceof Error ? err.message : "Could not save order");
    } finally {
      endMutation();
    }
  }

  function reorderPhoto(photoId: string, targetId: string) {
    if (photoId === targetId) return;
    const previous = [...photos];
    const next = [...photos];
    const from = next.findIndex((photo) => photo.id === photoId);
    const to = next.findIndex((photo) => photo.id === targetId);
    if (from < 0 || to < 0) return;
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    void saveOrder(next, previous);
  }

  function movePhoto(photoId: string, offset: number) {
    const index = photos.findIndex((photo) => photo.id === photoId);
    const target = photos[index + offset];
    if (target) reorderPhoto(photoId, target.id);
  }

  async function setCover(photoId: string) {
    if (!beginMutation()) return;
    const previous = photos;
    setPhotos(photos.map((photo) => ({ ...photo, isCover: photo.id === photoId })));
    setStatus("Saving cover...");
    try {
      const response = await authedFetch(getToken, `/api/galleries/${slug}/photos/curate`, {
        method: "PATCH",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ coverPhotoId: photoId })
      });
      await readJson(response);
      setStatus("Cover updated");
    } catch (err) {
      setPhotos(previous);
      setStatus(err instanceof Error ? err.message : "Could not update cover");
    } finally {
      endMutation();
    }
  }

  async function toggleHidden(photo: Photo) {
    if (!beginMutation()) return;
    const previous = photos;
    const nextHidden = !photo.isHidden;
    setPhotos(photos.map((item) => item.id === photo.id
      ? { ...item, isHidden: nextHidden, isCover: nextHidden ? false : item.isCover }
      : item));
    setStatus(nextHidden ? "Hiding photo..." : "Publishing photo...");
    try {
      const response = await authedFetch(getToken, `/api/galleries/${slug}/photos/${photo.id}`, {
        method: "PATCH",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ isHidden: nextHidden })
      });
      await readJson(response);
      setStatus(nextHidden ? "Photo hidden from visitors" : "Photo is public");
    } catch (err) {
      setPhotos(previous);
      setStatus(err instanceof Error ? err.message : "Could not update photo");
    } finally {
      endMutation();
    }
  }

  async function deletePhoto(photo: Photo) {
    if (!beginMutation()) return;
    setStatus("Deleting photo…");
    try {
      const response = await authedFetch(getToken, `/api/galleries/${slug}/photos/${photo.id}`, { method: "DELETE" });
      await readJson(response);
      setPhotos((current) => current.filter((item) => item.id !== photo.id));
      setDeletedPhotos((current) => [...current, photo]);
      setStatus("");
    } catch (err) {
      setStatus(err instanceof Error ? err.message : "Could not delete photo");
    } finally {
      endMutation();
    }
  }

  async function undoPhotoDeletion() {
    const photo = deletedPhotos[deletedPhotos.length - 1];
    if (!photo || !beginMutation()) return;
    try {
      await readJson(await authedFetch(getToken, `/api/galleries/${slug}/photos/${photo.id}/restore`, { method: "POST" }));
      const data = await fetchGalleryData(slug, await getToken());
      setPhotos(data.photos);
      setDeletedPhotos((current) => current.filter((item) => item.id !== photo.id));
      setStatus("Photo restored");
    } catch (err) {
      setStatus(err instanceof Error ? err.message : "Could not restore photo");
    } finally {
      endMutation();
    }
  }

  async function deleteGallery() {
    if (!beginMutation()) return;
    try {
      await readJson(await authedFetch(getToken, `/api/galleries/${slug}`, { method: "DELETE" }));
      setGalleryDeleted(true);
      setStatus("");
    } catch (err) {
      setStatus(err instanceof Error ? err.message : "Could not delete gallery");
    } finally {
      endMutation();
    }
  }

  async function undoGalleryDeletion() {
    if (!beginMutation()) return;
    try {
      await readJson(await authedFetch(getToken, `/api/galleries/${slug}/restore`, { method: "POST" }));
      const data = await fetchGalleryData(slug, await getToken());
      setPhotos(data.photos);
      setGalleryDeleted(false);
      setStatus("Gallery restored");
    } catch (err) {
      setStatus(err instanceof Error ? err.message : "Could not restore gallery");
    } finally {
      endMutation();
    }
  }

  if (galleryDeleted) {
    return (
      <ThemedShell theme={theme}>
        <header className="topbar"><a className="brand" href="/">Pics Lot</a><AuthControls /></header>
        <section className="empty-state" aria-label="Gallery deleted">
          <p role="status">Gallery deleted.</p>
          <div className="actions">
            <button type="button" onClick={undoGalleryDeletion} disabled={busy}><Undo2 size={17} />{busy ? "Restoring…" : "Undo"}</button>
            <a className="back-link" href="/">Back to galleries</a>
          </div>
          {status && <p className="error" role="alert">{status}</p>}
        </section>
      </ThemedShell>
    );
  }

  return (
    <ThemedShell theme={theme}>
      <header className="topbar gallery-topbar">
        <a className="brand" href="/">Pics Lot</a>
        <div className="topbar-actions">
          <button className="secondary compact-button" type="button" onClick={shareGallery}><Share2 size={17} />Share</button>
          {gallery.isOwner && (
            <nav aria-label="Gallery owner tools">
              <a className="nav-action" href={`/g/${slug}/upload`}><Upload size={17} />Upload</a>
              <a className="nav-action" href={`/settings/${slug}`}><Palette size={17} />Design</a>
              <button className={`secondary compact-button ${managing ? "is-active" : ""}`} type="button" onClick={() => setManaging((value) => !value)} disabled={busy}>
                {managing ? <Check size={17} /> : <LayoutDashboard size={17} />}
                {managing ? "Done" : "Manage"}
              </button>
            </nav>
          )}
          <AuthControls />
        </div>
      </header>

      <section className="gallery-hero">
        <div>
          <div className="hero-badges">
            <span><Images size={15} />{visiblePhotos.length} {visiblePhotos.length === 1 ? "photo" : "photos"}</span>
            {gallery.isOwner ? <span className="owner-badge">Owner view</span> : <span>Shared gallery</span>}
            {gallery.isOwner && photos.length > visiblePhotos.length && <span>{photos.length - visiblePhotos.length} hidden</span>}
          </div>
          <h1 tabIndex={-1}>{gallery.displayName}</h1>
          {managing && <p>Drag photos into order, choose a cover, or control what visitors see.</p>}
        </div>
        {status && <p className="inline-status" role="status">{status}</p>}
      </section>

      {managing && gallery.isOwner && (
        <div className="gallery-delete-action">
          <button className="secondary danger-button" type="button" onClick={deleteGallery} disabled={busy}><Trash2 size={16} />Delete gallery</button>
        </div>
      )}

      {displayPhotos.length === 0 ? (
        <EmptyGallery slug={slug} isOwner={Boolean(gallery.isOwner)} managing={managing} />
      ) : theme.layout === "scrapbook" && !managing ? (
        <ScrapbookGallery slug={slug} photos={visiblePhotos} theme={theme} />
      ) : (
        <PhotoGrid
          slug={slug}
          photos={displayPhotos}
          theme={theme}
          managing={managing}
          busy={busy}
          draggedId={draggedId}
          onDropPhoto={reorderPhoto}
          onMove={movePhoto}
          onCover={setCover}
          onToggleHidden={toggleHidden}
          onDelete={deletePhoto}
        />
      )}
      {deletedPhotos.length > 0 && (
        <div className="undo-notice" role="status">
          <span>Photo deleted.</span>
          <button className="secondary" type="button" onClick={undoPhotoDeletion} disabled={busy}><Undo2 size={16} />Undo</button>
        </div>
      )}
    </ThemedShell>
  );
}

type PhotoGridProps = {
  slug: string;
  photos: Photo[];
  theme: GalleryTheme;
  managing?: boolean;
  busy?: boolean;
  draggedId?: React.MutableRefObject<string | null>;
  onDropPhoto?: (photoId: string, targetId: string) => void;
  onMove?: (photoId: string, offset: number) => void;
  onCover?: (photoId: string) => void;
  onToggleHidden?: (photo: Photo) => void;
  onDelete?: (photo: Photo) => void;
};

export function PhotoGrid({
  slug,
  photos,
  theme,
  managing = false,
  busy = false,
  draggedId,
  onDropPhoto,
  onMove,
  onCover,
  onToggleHidden,
  onDelete
}: PhotoGridProps) {
  return (
    <section className={`photo-grid ${theme.layout} ${theme.density} ${managing ? "managing" : ""}`}>
      {photos.map((photo, index) => {
        const media = (
          <span className="photo-frame" style={{ "--photo-ratio": photoRatio(photo) } as CSSProperties}>
            <FramedPhoto
              photo={photo}
              badges={<>
                {photo.isHidden && <span className="hidden-label"><EyeOff size={14} />Hidden</span>}
                {photo.isCover && <span className="cover-label"><Star size={14} fill="currentColor" />Cover</span>}
              </>}
            />
          </span>
        );

        if (!managing) {
          return <a className="photo-tile" href={`/g/${slug}/photo/${photo.id}`} key={photo.id} aria-label={`Open photo ${index + 1}`}>{media}</a>;
        }

        function handleDrop(event: DragEvent<HTMLElement>) {
          event.preventDefault();
          const sourceId = draggedId?.current;
          if (sourceId && onDropPhoto) onDropPhoto(sourceId, photo.id);
        }

        return (
          <article
            className={`photo-tile manage-photo ${photo.isHidden ? "is-hidden" : ""}`}
            key={photo.id}
            draggable={!busy}
            onDragStart={() => { if (draggedId) draggedId.current = photo.id; }}
            onDragOver={(event) => event.preventDefault()}
            onDrop={handleDrop}
            onDragEnd={() => { if (draggedId) draggedId.current = null; }}
          >
            {media}
            <div className="photo-tools">
              <span className="drag-handle" title="Drag to reorder"><GripVertical size={17} /></span>
              <button className="icon-button" type="button" onClick={() => onMove?.(photo.id, -1)} disabled={busy || index === 0} aria-label="Move photo earlier" title="Move earlier"><MoveLeft size={17} /></button>
              <button className="icon-button" type="button" onClick={() => onMove?.(photo.id, 1)} disabled={busy || index === photos.length - 1} aria-label="Move photo later" title="Move later"><MoveRight size={17} /></button>
              <button className={`icon-button ${photo.isCover ? "is-active" : ""}`} type="button" onClick={() => onCover?.(photo.id)} disabled={busy || photo.isHidden} aria-label="Set as gallery cover" title="Set as cover"><Star size={17} fill={photo.isCover ? "currentColor" : "none"} /></button>
              <a className="icon-button" href={`/g/${slug}/photo/${photo.id}/edit`} aria-label="Edit photo" title="Edit photo"><Pencil size={17} /></a>
              <button className="icon-button" type="button" onClick={() => onToggleHidden?.(photo)} disabled={busy} aria-label={photo.isHidden ? "Show photo" : "Hide photo"} title={photo.isHidden ? "Show photo" : "Hide photo"}>{photo.isHidden ? <Eye size={17} /> : <EyeOff size={17} />}</button>
              <button className="icon-button danger-button" type="button" onClick={() => onDelete?.(photo)} disabled={busy} aria-label="Delete photo" title="Delete photo"><Trash2 size={17} /></button>
            </div>
          </article>
        );
      })}
    </section>
  );
}

function EmptyGallery({ slug, isOwner, managing }: { slug: string; isOwner: boolean; managing: boolean }) {
  return (
    <section className="empty-state">
      <ImagePlus size={38} />
      <h2>{managing ? "No photos to manage" : "No photos yet"}</h2>
      {isOwner ? <a className="link-button" href={`/g/${slug}/upload`}>Upload</a> : <p>This gallery is waiting for its first photograph.</p>}
    </section>
  );
}
